export type EstimateSourceType =
  | "listing"
  | "internal"
  | "public-api"
  | "heuristic";

export interface EstimateSource {
  label: string;
  sourceType: EstimateSourceType;
  provider?: string;
  accuracy: string;
}

export const ESTIMATE_SOURCES: EstimateSource[] = [
  {
    label: "Monthly rent",
    sourceType: "listing",
    provider: "Letting Engine listing data",
    accuracy: "Advertised rent from the listing (converted to PCM if quoted weekly).",
  },
  {
    label: "Deposit",
    sourceType: "listing",
    provider: "Letting Engine listing data",
    accuracy:
      "Uses the listing deposit when provided; otherwise estimated at five weeks' rent (typically within ±20%).",
  },
  {
    label: "Council tax",
    sourceType: "internal",
    accuracy:
      "Parsed from the listing description when available; otherwise estimated from a London postcode-district lookup table (typically within ±10–25% of the actual band).",
  },
  {
    label: "Utilities (gas, electric, water)",
    sourceType: "heuristic",
    accuracy:
      "Estimated from bedrooms and floor area using an internal formula (rough guide, often ±25–40%).",
  },
  {
    label: "Broadband",
    sourceType: "internal",
    accuracy: "Fixed allowance of £48/month — typical mid-range package; actual tariffs vary.",
  },
  {
    label: "TV licence",
    sourceType: "internal",
    accuracy: "Fixed at £14.95/month when included (current BBC licence fee rate).",
  },
  {
    label: "Contents insurance",
    sourceType: "internal",
    accuracy: "Fixed allowance of £15/month when included; actual premiums vary widely.",
  },
  {
    label: "Commute fares",
    sourceType: "heuristic",
    accuracy:
      "Simplified TfL-style fare bands (not live TfL pricing). Tube/mixed modes use ±15% variance; bus/walk/cycle are approximate.",
  },
  {
    label: "Weekly groceries",
    sourceType: "heuristic",
    accuracy:
      "Tiered spend bands (value / mid / premium) scaled by occupants and shopping frequency — indicative range only.",
  },
  {
    label: "Travel time & routes",
    sourceType: "public-api",
    provider: "Mapbox Directions API",
    accuracy:
      "Driving, walking, and cycling routes are generally reliable. Tube, bus, and mixed modes use driving-traffic as a proxy — not accurate for public transport.",
  },
  {
    label: "Postcode & address lookup",
    sourceType: "public-api",
    provider: "Mapbox Search Box API",
    accuracy: "UK postcode geocoding is generally accurate; always verify the resolved location.",
  },
  {
    label: "Nearby supermarkets",
    sourceType: "public-api",
    provider: "Mapbox Search Box API",
    accuracy:
      "Nearest matching store from Mapbox place data; opening hours and exact distance may differ.",
  },
  {
    label: "Neighbourhood POIs",
    sourceType: "public-api",
    provider: "Mapbox Search Box API",
    accuracy:
      "Points of interest from Mapbox place categories within ~1.5 km; completeness depends on Mapbox data coverage.",
  },
];

export const PUBLIC_API_PROVIDERS = [
  "Mapbox Directions API — route distance and duration",
  "Mapbox Search Box API — geocoding, supermarkets, and neighbourhood POIs",
] as const;

export const ESTIMATES_DISCLAIMER =
  "All figures shown are estimates for guidance only. Actual costs, distances, travel times, and charges prevail — please confirm with the agent, local authority, and service providers before making decisions.";
